import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, X, FileText, Image as ImageIcon } from "lucide-react";

const ICONS = { "application/pdf": FileText };

export default function FileUploader({ value = [], onChange, max = 10, accept = "application/pdf,image/*" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handle = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (value.length + files.length > max) {
      toast.error(`Maximum ${max} files allowed`);
      return;
    }
    setUploading(true);
    const next = [...value];
    for (const f of files) {
      if (f.size > 25 * 1024 * 1024) {
        toast.error(`${f.name} exceeds 25 MB`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        next.push(data);
      } catch (err) {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    onChange?.(next);
    if (inputRef.current) inputRef.current.value = "";
    setUploading(false);
  };

  const remove = (idx) => {
    const copy = [...value];
    copy.splice(idx, 1);
    onChange?.(copy);
  };

  return (
    <div className="space-y-2">
      <input ref={inputRef} type="file" multiple accept={accept} className="hidden" onChange={handle} data-testid="file-uploader-input" />
      <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="file-uploader-btn">
        <Upload className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Attach files"}
      </Button>
      {value.length > 0 && (
        <ul className="space-y-1.5">
          {value.map((f, i) => {
            const Icon = ICONS[f.content_type] || (f.content_type?.startsWith("image/") ? ImageIcon : FileText);
            return (
              <li key={f.file_id || i} className="flex items-center gap-2 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <Icon className="h-4 w-4 text-slate-500" />
                <span className="flex-1 truncate" data-testid={`file-uploader-name-${i}`}>{f.original_filename || f.name}</span>
                <span className="text-xs text-slate-400">{((f.size || 0) / 1024).toFixed(0)} KB</span>
                <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-red-500" data-testid={`file-uploader-remove-${i}`}>
                  <X className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
